import { useState } from 'react';
import { Icons } from '../components/Icons';
import { MonitorTabs } from '../components/monitor';
import { ResponsiveTable } from '../components/ui/ResponsiveTable';
import { ACCOUNT_COLORS } from '../config/monitorColors';

// V2.I.bonus.b : TaxStrategies palette was a byte-identical copy of
// ACCOUNT_COLORS — deduped via the central config. Local alias kept so
// the rest of the file can keep reading TAX_COLORS.x without churn.
const TAX_COLORS = ACCOUNT_COLORS;

type TabType = 'strategies' | 'platforms' | 'cards' | 'cases';

const TABS: { id: TabType; label: string }[] = [
  { id: 'strategies', label: '📋 Stratégies' },
  { id: 'platforms', label: '🏦 Plateformes' },
  { id: 'cards', label: '💳 Cartes Crypto' },
  { id: 'cases', label: '💼 Cas Pratiques' },
];

/**
 * Tax Strategies & Fiscal Optimization Guide
 *
 * Guide complet basé sur guide-lombard.md
 * Sources : Ordonnance 2025-391, Article 150 VH bis CGI, MiCA
 */

interface Strategy {
  id: string;
  title: string;
  icon: string;
  difficulty: 'Débutant' | 'Intermédiaire' | 'Avancé';
  taxSavings: string;
  risk: 'Faible' | 'Moyen' | 'Élevé';
  description: string;
  pros: string[];
  cons: string[];
  detailedContent?: React.ReactNode;
}

interface Platform {
  name: string;
  jurisdiction: string;
  lvtBTC: string;
  lvtETH: string;
  rate: string;
  minAmount: string;
  kyc: boolean;
  custodial: boolean;
}

interface CryptoCard {
  name: string;
  cashback: string;
  stakingRequired: string;
  fees: string;
  availability: string;
  verdict: string;
}

const LOMBARD_PLATFORMS: Platform[] = [
  {
    name: 'Swissquote (Suisse)',
    jurisdiction: '🇨🇭 FINMA',
    lvtBTC: '20%',
    lvtETH: '10%',
    rate: '~4.31% EUR',
    minAmount: '10 000 CHF',
    kyc: true,
    custodial: true,
  },
  {
    name: 'Mt Pelerin (Suisse)',
    jurisdiction: '🇨🇭 Suisse',
    lvtBTC: 'Variable',
    lvtETH: 'Variable',
    rate: 'Variable',
    minAmount: 'N/A',
    kyc: true,
    custodial: true,
  },
  {
    name: 'Legasi (France)',
    jurisdiction: '🇫🇷 AMF',
    lvtBTC: 'Variable',
    lvtETH: 'Variable',
    rate: 'Variable',
    minAmount: 'Variable',
    kyc: true,
    custodial: true,
  },
  {
    name: 'AAVE Protocol (DeFi)',
    jurisdiction: '⚡ Décentralisé',
    lvtBTC: '~70%',
    lvtETH: '~80%',
    rate: '3-15% (variable)',
    minAmount: 'Aucun',
    kyc: false,
    custodial: false,
  },
];

const CRYPTO_CARDS: CryptoCard[] = [
  {
    name: 'Bybit Card',
    cashback: '10% (meilleur)',
    stakingRequired: 'Variable',
    fees: '0€/an',
    availability: 'EEA (sauf IE/HR)',
    verdict: '🏆 Meilleure option Europe 2025',
  },
  {
    name: 'Crypto.com Jade Green',
    cashback: '3%',
    stakingRequired: '~5 000€ CRO',
    fees: '0€/an',
    availability: 'Global (sauf USA)',
    verdict: '✅ Excellent mid-tier avec Spotify',
  },
  {
    name: 'Bleap Mastercard',
    cashback: '2% USDC',
    stakingRequired: 'Aucun',
    fees: '0€ (tous frais)',
    availability: 'Europe',
    verdict: '✅ Meilleur sans staking',
  },
  {
    name: 'Revolut Metal',
    cashback: '0.1-1%',
    stakingRequired: 'Aucun',
    fees: '12.99€/mois',
    availability: 'Europe/UK',
    verdict: '⚠️ Faible cashback, mais régulé',
  },
  {
    name: 'Binance Card',
    cashback: '2-8%',
    stakingRequired: '~300€+ BNB',
    fees: '0€/an',
    availability: '❌ Brésil uniquement',
    verdict: '❌ Shutdown Europe 2023',
  },
];

const STRATEGIES: Strategy[] = [
  {
    id: 'lombard',
    title: 'Crédit Lombard',
    icon: '🏦',
    difficulty: 'Intermédiaire',
    taxSavings: '100% (pas de vente)',
    risk: 'Moyen',
    description: 'Emprunter de la fiat en utilisant vos cryptos comme collatéral, sans les vendre.',
    pros: [
      'Pas de fait générateur fiscal (pas de vente)',
      'Conservez vos positions crypto',
      'Profitez de la hausse future',
      'Liquidité immédiate sans vendre',
      'Cadre légal : Ordonnance 2025-391',
    ],
    cons: [
      'Risque de liquidation si le prix baisse',
      'Intérêts à payer sur le prêt (4-8%/an)',
      'Nécessite un collatéral important (150-200%)',
      'LTV faible pour BTC/ETH (20-40%)',
      'Zone grise fiscale persistante',
    ],
  },
  {
    id: 'aave',
    title: 'AAVE Protocol',
    icon: '⚡',
    difficulty: 'Avancé',
    taxSavings: '100% (lending)',
    risk: 'Moyen',
    description: 'Protocole DeFi pour prêter/emprunter des cryptos de manière décentralisée.',
    pros: [
      'Gains passifs en prêtant vos cryptos',
      'Emprunter sans KYC',
      'LTV élevé (70-80% pour ETH/BTC)',
      'Contrôle total de vos fonds (non-custodial)',
      'TVL : &gt;40B€ (protocole le plus utilisé)',
      '24/7 disponibilité',
    ],
    cons: [
      'Risque smart contract',
      'Complexité technique élevée',
      'Gas fees Ethereum',
      'Nécessite compréhension DeFi',
      'Pas de recours en cas de hack',
    ],
  },
  {
    id: 'cards',
    title: 'Cartes Crypto',
    icon: '💳',
    difficulty: 'Débutant',
    taxSavings: 'Variable (dépend usage)',
    risk: 'Faible',
    description: 'Dépenser vos cryptos via carte bancaire et profiter de cashback.',
    pros: [
      'Simplicité d\'utilisation',
      'Cashback en crypto (jusqu\'à 10%)',
      'Accepté partout',
      'Rebates (Netflix, Spotify, etc.)',
      'Pas de complexité technique',
    ],
    cons: [
      'Conversion automatique = vente fiscale',
      'Frais de conversion (0.5-2.49%)',
      'Staking souvent requis pour bons taux',
      'KYC obligatoire',
      'Volatilité token natif (CRO, BNB, PLU)',
    ],
  },
  {
    id: 'stablecoins',
    title: 'Stablecoins Lending',
    icon: '💵',
    difficulty: 'Débutant',
    taxSavings: 'Intérêts taxés uniquement',
    risk: 'Faible',
    description: 'Convertir en stablecoins et générer des intérêts sans exposition volatilité.',
    pros: [
      'Pas de volatilité',
      'Rendements passifs (5-12% APY)',
      'Plus simple que DeFi',
      'Conversion crypto → stable non imposée',
      'Préserve capital en bear market',
    ],
    cons: [
      'Rendements taxés comme revenus',
      'Risque dépeg du stablecoin',
      'Risque plateforme (CeFi)',
      'Perte d\'exposition crypto',
      'APY en baisse (2025)',
    ],
  },
];

export function TaxStrategies() {
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabType>('strategies');

  // Calculateur simple
  const [portfolioValue, setPortfolioValue] = useState<number>(100000);
  const [purchasePrice, setPurchasePrice] = useState<number>(60000);
  const [liquidityNeeded, setLiquidityNeeded] = useState<number>(30000);

  const strategy = selectedStrategy ? STRATEGIES.find(s => s.id === selectedStrategy) : null;

  const calculateTaxScenarios = () => {
    const plusValue = portfolioValue - purchasePrice;
    const plusValueProportional = (liquidityNeeded / portfolioValue) * plusValue;
    const flatTax = plusValueProportional * 0.30;

    // Crédit Lombard (LTV 40%)
    const lombardLoan = portfolioValue * 0.40;
    const lombardInterest = lombardLoan * 0.06;

    // AAVE (LTV 70%)
    const aaveLoan = portfolioValue * 0.70;
    const aaveInterest = aaveLoan * 0.05;

    return {
      directSale: {
        tax: flatTax,
        netReceived: liquidityNeeded - flatTax,
        cryptoRemaining: portfolioValue - liquidityNeeded,
      },
      lombard: {
        loan: lombardLoan,
        interest: lombardInterest,
        taxSaved: flatTax - lombardInterest,
        cryptoRemaining: portfolioValue,
      },
      aave: {
        loan: aaveLoan,
        interest: aaveInterest,
        taxSaved: flatTax - aaveInterest,
        cryptoRemaining: portfolioValue,
      },
    };
  };

  const scenarios = calculateTaxScenarios();

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
          }}>
            🎓
          </div>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: '700', margin: 0 }}>
              Guide Fiscal & Stratégies Crypto
            </h1>
            <p style={{ color: 'var(--muted)', margin: '4px 0 0 0' }}>
              Guide complet basé sur l'Ordonnance 2025-391 et l'Article 150 VH bis CGI
            </p>
          </div>
        </div>

        {/* Warning Banner */}
        <div style={{
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          gap: '12px',
          alignItems: 'start',
        }}>
          <Icons.AlertCircle size={20} style={{ color: '#fbbf24', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
            <strong style={{ color: '#fbbf24' }}>Avertissement Légal :</strong>
            <br />
            Ces informations sont fournies à titre éducatif uniquement. <strong>Consultez toujours un conseiller fiscal agréé</strong> avant d'implémenter une stratégie. Les lois fiscales varient selon les juridictions et évoluent constamment. Zone grise persistante sur le crédit lombard crypto en 2025.
          </div>
        </div>
      </div>

      {/* Tax Context - France/EU */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🇫🇷</span> Contexte Fiscal France/UE
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          {/* Flat Tax */}
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
            padding: '16px',
          }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444', marginBottom: '4px' }}>
              30%
            </div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Flat Tax (France)
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.5' }}>
              12.8% impôt + 17.2% prélèvements sociaux sur plus-values crypto
            </div>
          </div>

          {/* Fait Générateur */}
          <div style={{
            background: 'rgba(99, 102, 241, 0.1)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            borderRadius: '8px',
            padding: '16px',
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#6366f1', marginBottom: '4px' }}>
              Fiat = Taxe
            </div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Fait Générateur
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.5' }}>
              Conversion vers fiat (EUR, USD...) ou achat biens/services. Les échanges crypto-crypto ne sont PAS taxables (MiCA).
            </div>
          </div>

          {/* Objectif */}
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: '8px',
            padding: '16px',
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#22c55e', marginBottom: '4px' }}>
              0% Tax
            </div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Objectif
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.5' }}>
              Profiter de ses gains sans vendre grâce au crédit lombard
            </div>
          </div>
        </div>

        {/* Visual Flow */}
        <div style={{
          background: 'var(--bg)',
          borderRadius: '8px',
          padding: '20px',
          fontFamily: 'monospace',
          fontSize: '13px',
          lineHeight: '1.8',
        }}>
          <div style={{ color: 'var(--muted)', marginBottom: '12px' }}>📊 Scénario Classique (MAUVAIS) :</div>
          <div>
            <span style={{ color: '#ef4444' }}>Achat BTC 20k€</span>
            {' → '}
            <span style={{ color: '#f59e0b' }}>+200% (60k€)</span>
            {' → '}
            <span style={{ color: '#ef4444' }}>VENTE</span>
            {' → '}
            <span style={{ color: '#dc2626', fontWeight: 'bold' }}>-12k€ TAXE 💸 (30%)</span>
          </div>

          <div style={{ margin: '16px 0', borderTop: '1px solid var(--border-color)' }} />

          <div style={{ color: 'var(--muted)', marginBottom: '12px' }}>✅ Avec Crédit Lombard (BON) :</div>
          <div>
            <span style={{ color: '#22c55e' }}>Achat BTC 20k€</span>
            {' → '}
            <span style={{ color: '#f59e0b' }}>+200% (60k€)</span>
            {' → '}
            <span style={{ color: '#22c55e' }}>CRÉDIT LOMBARD (40% LTV)</span>
            {' → '}
            <span style={{ color: '#10b981', fontWeight: 'bold' }}>0€ TAXE + 24k€ LIQUIDITÉ ✨</span>
          </div>
          <div style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '8px' }}>
            ↳ Intérêts : ~1440€/an (6%) • Économie nette année 1 : 12k€ - 1.4k€ = 10.6k€
          </div>
        </div>

        {/* Cadre Légal */}
        <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(99, 102, 241, 0.05)', borderRadius: '8px', fontSize: '13px' }}>
          <strong>📜 Cadre Légal :</strong>
          <ul style={{ margin: '8px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
            <li><strong>Ordonnance du 8 mai 2025</strong> (Loi DDADUE 5 - n°2025-391) : Légalisation du nantissement de crypto-actifs</li>
            <li><strong>Article 150 VH bis du CGI</strong> : Fait générateur = conversion vers fiat ou achat biens/services (échanges crypto-crypto exclus)</li>
            <li><strong>MiCA (EU)</strong> : Harmonisation européenne confirmant que les échanges crypto-crypto ne sont pas des faits générateurs fiscaux</li>
            <li><strong>⚠️ Zone grise</strong> : Le nantissement non-translatif n'est pas explicitement taxable, mais consultation fiscaliste recommandée</li>
          </ul>
        </div>
      </div>

      <MonitorTabs
        tabs={TABS}
        activeTab={activeTab}
        onChange={setActiveTab}
        colors={TAX_COLORS}
        ariaLabel="Tax Strategies sections"
      />

      {/* STRATEGIES TAB */}
      {activeTab === 'strategies' && (
        <>
          {/* Strategies Grid */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
              Stratégies Disponibles
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
              {STRATEGIES.map((strat) => (
                <div
                  key={strat.id}
                  className="card"
                  style={{
                    cursor: 'pointer',
                    border: selectedStrategy === strat.id ? '2px solid var(--accent)' : undefined,
                    transition: 'all 0.2s',
                  }}
                  onClick={() => setSelectedStrategy(strat.id)}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'start', gap: '12px', marginBottom: '16px' }}>
                    <div style={{
                      fontSize: '32px',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'var(--bg)',
                      borderRadius: '12px',
                    }}>
                      {strat.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 4px 0' }}>
                        {strat.title}
                      </h3>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: strat.difficulty === 'Débutant' ? 'rgba(34, 197, 94, 0.1)' :
                                     strat.difficulty === 'Intermédiaire' ? 'rgba(251, 191, 36, 0.1)' :
                                     'rgba(239, 68, 68, 0.1)',
                          color: strat.difficulty === 'Débutant' ? '#22c55e' :
                                 strat.difficulty === 'Intermédiaire' ? '#fbbf24' :
                                 '#ef4444',
                        }}>
                          {strat.difficulty}
                        </span>
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: 'rgba(99, 102, 241, 0.1)',
                          color: '#6366f1',
                        }}>
                          Risque: {strat.risk}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '16px' }}>
                    {strat.description}
                  </p>

                  {/* Tax Savings */}
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '12px',
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                      Économie Fiscale
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#22c55e' }}>
                      {strat.taxSavings}
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => setSelectedStrategy(strat.id)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: 'none',
                      background: selectedStrategy === strat.id ? 'var(--accent)' : 'var(--bg)',
                      color: selectedStrategy === strat.id ? 'white' : 'var(--text)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {selectedStrategy === strat.id ? '✓ Sélectionné' : 'En savoir plus'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Strategy View */}
          {strategy && (
            <div className="card" style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <span style={{ fontSize: '32px' }}>{strategy.icon}</span>
                <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0 }}>
                  {strategy.title} - Guide Détaillé
                </h2>
              </div>

              {/* Pros & Cons */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                {/* Pros */}
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icons.CheckCircle size={20} />
                    Avantages
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {strategy.pros.map((pro, idx) => (
                      <li key={idx} style={{ fontSize: '14px', lineHeight: '1.8', color: 'var(--text)' }}>
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cons */}
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icons.X size={20} />
                    Inconvénients
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {strategy.cons.map((con, idx) => (
                      <li key={idx} style={{ fontSize: '14px', lineHeight: '1.8', color: 'var(--text)' }}>
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Strategy-specific content */}
              {strategy.id === 'lombard' && (
                <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                    📊 Ratios LTV Typiques (2025)
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '14px' }}>
                    <div><strong>Bitcoin (BTC):</strong> 20-40%</div>
                    <div><strong>Ethereum (ETH):</strong> 10-30%</div>
                    <div><strong>Stablecoins:</strong> 40-90%</div>
                    <div><strong>Actions/ETF:</strong> 30-70%</div>
                  </div>
                  <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', fontSize: '13px' }}>
                    <strong>⚠️ Risque critique :</strong> Appel de marge à ~80%. Si collatéral baisse, vous devez apporter des fonds ou rembourser, sinon liquidation forcée = imposition inévitable.
                  </div>
                </div>
              )}

              {strategy.id === 'aave' && (
                <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                    ⚡ AAVE Protocol : Caractéristiques Clés
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                    <p><strong>TVL:</strong> {'>'} 40 milliards d'euros (novembre 2025) - Plus grande plateforme DeFi</p>
                    <p><strong>Blockchains:</strong> Ethereum + 12 autres réseaux (Polygon, Arbitrum, Optimism, Avalanche, etc.)</p>
                    <p><strong>Actifs supportés:</strong> 30+ (BTC, ETH, USDT, USDC, DAI, AVAX, etc.)</p>
                    <p><strong>Innovations:</strong></p>
                    <ul style={{ marginTop: '8px' }}>
                      <li><strong>Flash Loans:</strong> Emprunts sans collatéral (remboursement même transaction)</li>
                      <li><strong>GHO Stablecoin:</strong> Stablecoin décentralisé surcollatéralisé</li>
                      <li><strong>Health Factor:</strong> Indicateur de sécurité ({"<"}1 = risque liquidation)</li>
                    </ul>
                    <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px' }}>
                      <strong>⚠️ Fiscalité France:</strong> Dépôt = pas de fait générateur. Emprunt = zone grise. Liquidation = taxable si conversion fiat. Consultez un fiscaliste.
                    </div>
                  </div>
                </div>
              )}

              {strategy.id === 'cards' && (
                <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '8px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                    💳 Top Cartes Crypto 2025
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                    <p><strong>🏆 Bybit Card (Europe):</strong> Cashback 10% + 100% rebates Netflix/Spotify/Amazon Prime</p>
                    <p><strong>✅ Crypto.com Jade Green:</strong> 3% cashback + Spotify rebate (stake 5 000€ CRO)</p>
                    <p><strong>✅ Bleap Mastercard:</strong> 2% USDC sans staking, 0€ tous frais</p>
                    <p><strong>⚠️ Revolut Metal:</strong> 0.1-1% cashback, 12.99€/mois (régulé mais faible ROI)</p>
                    <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px' }}>
                      <strong>⚠️ Important:</strong> Chaque paiement par carte = conversion crypto→fiat = vente fiscale taxable à 30%. Tenez compte de ceci dans vos déclarations.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* PLATFORMS TAB */}
      {activeTab === 'platforms' && (
        <div className="card" style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
            🏦 Plateformes Crédit Lombard & DeFi
          </h2>

          {/* V2.I.7 — migrated to ResponsiveTable : table desktop, cards mobile. */}
          <ResponsiveTable<Platform>
            data={LOMBARD_PLATFORMS}
            keyExtractor={(_, idx) => idx}
            columns={[
              {
                key: 'name',
                header: 'Plateforme',
                isTitle: true,
                render: (p) => <span style={{ fontWeight: 600 }}>{p.name}</span>,
              },
              { key: 'jurisdiction', header: 'Juridiction', align: 'center', render: (p) => p.jurisdiction },
              {
                key: 'lvtBTC',
                header: 'LTV BTC',
                align: 'center',
                render: (p) => <span style={{ color: '#22c55e', fontWeight: 600 }}>{p.lvtBTC}</span>,
              },
              {
                key: 'lvtETH',
                header: 'LTV ETH',
                align: 'center',
                render: (p) => <span style={{ color: '#22c55e', fontWeight: 600 }}>{p.lvtETH}</span>,
              },
              { key: 'rate', header: 'Taux', align: 'center', render: (p) => p.rate },
              { key: 'minAmount', header: 'Minimum', align: 'center', render: (p) => p.minAmount },
              { key: 'kyc', header: 'KYC', align: 'center', render: (p) => (p.kyc ? '✅' : '❌') },
              { key: 'custodial', header: 'Custodial', align: 'center', render: (p) => (p.custodial ? '✅' : '❌') },
            ]}
          />

          {/* Comparatif CeFi vs DeFi */}
          <div style={{ marginTop: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
              🔄 CeFi vs DeFi : Quel Choix ?
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              {/* CeFi */}
              <div style={{ padding: '16px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#6366f1' }}>
                  🏦 CeFi (Banques)
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                  <p><strong>Avantages:</strong></p>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Régulé (FINMA, AMF)</li>
                    <li>Support client</li>
                    <li>Sécurité bancaire</li>
                    <li>Recours juridique</li>
                  </ul>
                  <p><strong>Inconvénients:</strong></p>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>KYC obligatoire</li>
                    <li>Custodial</li>
                    <li>LTV faible (20-40%)</li>
                    <li>Minimum élevé (10k€+)</li>
                  </ul>
                </div>
              </div>

              {/* DeFi */}
              <div style={{ padding: '16px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#22c55e' }}>
                  ⚡ DeFi (AAVE)
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                  <p><strong>Avantages:</strong></p>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Pas de KYC</li>
                    <li>Non-custodial</li>
                    <li>LTV élevé (70-80%)</li>
                    <li>Pas de minimum</li>
                    <li>24/7 disponible</li>
                  </ul>
                  <p><strong>Inconvénients:</strong></p>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Risque smart contract</li>
                    <li>Pas de recours</li>
                    <li>Complexité technique</li>
                    <li>Gas fees Ethereum</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Autres plateformes DeFi */}
          <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(99, 102, 241, 0.05)', borderRadius: '8px', fontSize: '14px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              🔗 Autres Protocoles DeFi Lending
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <div><strong>Compound Finance:</strong> TVL ~3B$ (pionnier)</div>
              <div><strong>MakerDAO:</strong> TVL ~7B$ (DAI stablecoin)</div>
              <div><strong>Curve Finance:</strong> TVL ~4B$ (stablecoins)</div>
              <div><strong>Lido Finance:</strong> Liquid staking ETH (~2.6% APY)</div>
            </div>
          </div>
        </div>
      )}

      {/* CARDS TAB */}
      {activeTab === 'cards' && (
        <div className="card" style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
            💳 Comparatif Cartes Crypto 2025
          </h2>

          {/* V2.I.7 — migrated to ResponsiveTable. */}
          <div style={{ marginBottom: '24px' }}>
            <ResponsiveTable<CryptoCard>
              data={CRYPTO_CARDS}
              keyExtractor={(_, idx) => idx}
              columns={[
                {
                  key: 'name',
                  header: 'Carte',
                  isTitle: true,
                  render: (c) => <span style={{ fontWeight: 600 }}>{c.name}</span>,
                },
                {
                  key: 'cashback',
                  header: 'Cashback',
                  align: 'center',
                  render: (c) => <span style={{ color: '#22c55e', fontWeight: 600 }}>{c.cashback}</span>,
                },
                { key: 'stakingRequired', header: 'Staking', align: 'center', render: (c) => c.stakingRequired },
                { key: 'fees', header: 'Frais', align: 'center', render: (c) => c.fees },
                { key: 'availability', header: 'Disponibilité', render: (c) => c.availability },
                {
                  key: 'verdict',
                  header: 'Verdict',
                  render: (c) => <span style={{ fontSize: '12px' }}>{c.verdict}</span>,
                },
              ]}
            />
          </div>

          {/* Calculs détaillés Crypto.com */}
          <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
              📊 Exemple Calcul ROI : Crypto.com Jade Green
            </h3>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <p><strong>Investissement Initial:</strong></p>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Stake : 5 000€ en CRO</li>
                <li>Période de blocage : 180 jours</li>
              </ul>

              <p><strong>Récompenses Annuelles:</strong></p>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Staking APY (4%) : 200€/an</li>
                <li>Cashback 3% sur 20 000€ dépenses : 600€/an</li>
                <li>Spotify rebate : 168€/an (13.99€/mois)</li>
                <li><strong>Total : 968€/an</strong></li>
              </ul>

              <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px' }}>
                <strong>ROI : 968€ / 5 000€ = 19.4% annuel</strong>
                <br />
                <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  ⚠️ Ne tient pas compte de la volatilité du CRO
                </span>
              </div>
            </div>
          </div>

          {/* Avertissement fiscal */}
          <div style={{ padding: '16px', background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '8px', fontSize: '14px' }}>
            <strong>⚠️ Fiscalité des Cartes Crypto :</strong>
            <p style={{ margin: '8px 0', lineHeight: '1.6' }}>
              Chaque paiement par carte crypto constitue une <strong>vente fiscale</strong> soumise à la flat tax de 30%.
              Le cashback reçu en crypto est également imposable lors de sa conversion en fiat ou utilisation pour achats.
              Tenez un registre précis de vos transactions pour vos déclarations fiscales (formulaire 2086).
            </p>
          </div>
        </div>
      )}

      {/* CASES TAB */}
      {activeTab === 'cases' && (
        <>
          {/* Calculateur */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
              🧮 Calculateur de Scénarios Fiscaux
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Valeur actuelle du portefeuille (€)
                </label>
                <input
                  type="number"
                  value={portfolioValue}
                  onChange={(e) => setPortfolioValue(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Prix d'achat total (€)
                </label>
                <input
                  type="number"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Liquidité nécessaire (€)
                </label>
                <input
                  type="number"
                  value={liquidityNeeded}
                  onChange={(e) => setLiquidityNeeded(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: '14px',
                  }}
                />
              </div>
            </div>

            {/* Results */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              {/* Vente Directe */}
              <div style={{
                padding: '16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#ef4444' }}>
                  ❌ Vente Directe
                </h3>
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <p><strong>Impôt immédiat :</strong> {scenarios.directSale.tax.toLocaleString()}€</p>
                  <p><strong>Net reçu :</strong> {scenarios.directSale.netReceived.toLocaleString()}€</p>
                  <p><strong>Cryptos restants :</strong> {scenarios.directSale.cryptoRemaining.toLocaleString()}€</p>
                </div>
              </div>

              {/* Crédit Lombard */}
              <div style={{
                padding: '16px',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.2)',
                borderRadius: '8px',
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#fbbf24' }}>
                  ⚠️ Crédit Lombard (40% LTV, 6%)
                </h3>
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <p><strong>Prêt accordé :</strong> {scenarios.lombard.loan.toLocaleString()}€</p>
                  <p><strong>Intérêts an 1 :</strong> {scenarios.lombard.interest.toLocaleString()}€</p>
                  <p><strong>Économie vs vente :</strong> <span style={{ color: '#22c55e', fontWeight: '700' }}>{scenarios.lombard.taxSaved.toLocaleString()}€</span></p>
                  <p><strong>Cryptos conservés :</strong> {scenarios.lombard.cryptoRemaining.toLocaleString()}€ ✅</p>
                </div>
              </div>

              {/* AAVE */}
              <div style={{
                padding: '16px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                borderRadius: '8px',
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#22c55e' }}>
                  ✅ AAVE DeFi (70% LTV, 5%)
                </h3>
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <p><strong>Prêt accordé :</strong> {scenarios.aave.loan.toLocaleString()}€</p>
                  <p><strong>Intérêts an 1 :</strong> {scenarios.aave.interest.toLocaleString()}€</p>
                  <p><strong>Économie vs vente :</strong> <span style={{ color: '#22c55e', fontWeight: '700' }}>{scenarios.aave.taxSaved.toLocaleString()}€</span></p>
                  <p><strong>Cryptos conservés :</strong> {scenarios.aave.cryptoRemaining.toLocaleString()}€ ✅</p>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '8px', fontSize: '13px' }}>
              <strong>💡 Interprétation :</strong> Ces calculs ne tiennent pas compte de la volatilité crypto, des appels de marge potentiels,
              ni des gas fees (AAVE). Consultez un fiscaliste avant toute décision.
            </div>
          </div>

          {/* Cas Pratiques */}
          <div className="card">
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
              💼 Cas Pratiques par Portefeuille
            </h2>

            {/* 100k€ */}
            <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#6366f1' }}>
                📊 Portefeuille 100 000€
              </h3>

              <div style={{ fontSize: '14px', lineHeight: '1.8', marginBottom: '16px' }}>
                <p><strong>Profil type :</strong> 30-45 ans, expérience intermédiaire, horizon 3-5 ans</p>

                <p style={{ marginTop: '12px' }}><strong>Allocation recommandée :</strong></p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Bitcoin (BTC) : 45 000€ (45%)</li>
                  <li>Ethereum (ETH) : 25 000€ (25%)</li>
                  <li>Stablecoins (USDC/USDT) : 15 000€ (15%)</li>
                  <li>Altcoins majeurs : 10 000€ (10%) - SOL, AVAX, MATIC</li>
                  <li>DeFi/Yield (AAVE) : 5 000€ (5%)</li>
                </ul>

                <p style={{ marginTop: '12px' }}><strong>Stratégie fiscale :</strong></p>
                <p>Besoin de liquidité 30 000€ :</p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li><strong>❌ Vente directe :</strong> 3 600€ d'impôts</li>
                  <li><strong>✅ Crédit Lombard (25% LTV):</strong> 17 500€ prêt, 754€ intérêts/an → Économie nette: 2 846€</li>
                  <li><strong>✅ AAVE (70% LTV):</strong> 17 500€ prêt, 875€ intérêts/an → Économie nette: 2 725€</li>
                </ul>

                <p style={{ marginTop: '12px' }}><strong>Carte recommandée :</strong></p>
                <p><strong>Crypto.com Ruby Steel</strong> - Stake 500€ CRO, cashback 2%, ROI 60% si dépenses 15k€/an</p>
              </div>
            </div>

            {/* 200k€ */}
            <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--bg)', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#10b981' }}>
                📊 Portefeuille 200 000€
              </h3>

              <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                <p><strong>Profil type :</strong> 35-50 ans, patrimoine diversifié, objectif revenus passifs</p>

                <p style={{ marginTop: '12px' }}><strong>Allocation recommandée :</strong></p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Bitcoin (BTC) : 80 000€ (40%)</li>
                  <li>Ethereum (ETH) : 60 000€ (30%)</li>
                  <li>Stablecoins lending : 30 000€ (15%)</li>
                  <li>Altcoins : 20 000€ (10%)</li>
                  <li>DeFi/Yield : 10 000€ (5%)</li>
                </ul>

                <p style={{ marginTop: '12px' }}><strong>Stratégie fiscale avancée :</strong></p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Swissquote : Accès à conditions plus favorables ({'>'}250k€ total avec fiat)</li>
                  <li>AAVE : Emprunter USDC avec ETH comme collatéral</li>
                  <li>Stablecoins lending : 8-12% APY sur 30k€ = 2 400-3 600€/an</li>
                </ul>

                <p style={{ marginTop: '12px' }}><strong>Carte recommandée :</strong></p>
                <p><strong>Crypto.com Jade Green</strong> - Stake 5 000€, cashback 3% + Spotify, ROI 19.4%</p>
              </div>
            </div>

            {/* 500k€+ */}
            <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#f59e0b' }}>
                📊 Portefeuille 500 000€+
              </h3>

              <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                <p><strong>Profil type :</strong> High net worth, gestion patrimoniale, fiscalité optimisée</p>

                <p style={{ marginTop: '12px' }}><strong>Stratégies premium :</strong></p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li><strong>Banques luxembourgeoises :</strong> Quintet, EFG, UBS (seuil {'>'} 1M€)</li>
                  <li><strong>Structuration fiscale :</strong> Consultation avocat fiscaliste spécialisé obligatoire</li>
                  <li><strong>Demande de rescrit fiscal :</strong> Sécuriser position avec administration</li>
                  <li><strong>Diversification géographique :</strong> Multi-juridictions (Suisse, Luxembourg)</li>
                  <li><strong>Expatriation fiscale :</strong> Portugal, Allemagne, Belgique, Île Maurice</li>
                </ul>

                <p style={{ marginTop: '12px' }}><strong>Carte recommandée :</strong></p>
                <p><strong>Crypto.com Icy White</strong> - Stake 50 000€, cashback 5%, Netflix + Spotify + Priority Pass, ROI 10%+</p>

                <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px' }}>
                  <strong>⚠️ Important :</strong> À partir de 500k€, la structuration juridique et fiscale devient critique.
                  Ne procédez qu'avec accompagnement professionnel (fiscaliste crypto, avocat patrimonialiste).
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Resources Section */}
      <div className="card">
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.BookOpen size={20} />
          Ressources & Documentation
        </h2>

        <div style={{ display: 'grid', gap: '12px' }}>
          <a
            href="https://www.impots.gouv.fr"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              background: 'var(--bg)',
              borderRadius: '8px',
              textDecoration: 'none',
              color: 'var(--text)',
              transition: 'all 0.2s',
            }}
          >
            <Icons.ExternalLink size={16} style={{ color: 'var(--accent)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>Impots.gouv.fr</div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Documentation officielle fiscalité crypto</div>
            </div>
          </a>

          <div style={{
            padding: '16px',
            background: 'rgba(99, 102, 241, 0.1)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            borderRadius: '8px',
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              📚 Source de ce Guide
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6' }}>
              Ce guide est basé sur <strong>guide-lombard.md</strong>, un document complet de plus de 3000 lignes couvrant :
            </div>
            <ul style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '8px', paddingLeft: '20px' }}>
              <li>Cadre juridique français (Ordonnance 2025-391, Article 150 VH bis CGI)</li>
              <li>Réglementation européenne (MiCA)</li>
              <li>Comparatif détaillé de toutes les plateformes et cartes</li>
              <li>Cas pratiques par tranche de portefeuille (100k€, 200k€, 300k€, 500k€+)</li>
              <li>Checklists, calculs ROI, optimisations fiscales légales</li>
            </ul>
          </div>

          {/* Legal Warning */}
          <div style={{
            padding: '16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#ef4444' }}>
              ⚠️ Checklist Avant Crédit Lombard
            </div>
            <ul style={{ fontSize: '13px', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>Consultation fiscaliste spécialisé crypto (OBLIGATOIRE)</li>
              <li>Demande de rescrit fiscal (si gros montants)</li>
              <li>Vérification type de nantissement (non-translatif préféré)</li>
              <li>Compréhension mécanisme appel de marge</li>
              <li>Fonds de réserve pour margin calls</li>
              <li>Calcul coût total (intérêts sur durée prévue)</li>
              <li>Lecture complète du contrat</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
